/**
 * Flotten-Tab im PlanetMenu.
 * Render als Hintergrund, Name/Klasse oben, Produktion/Stats als Glass-Karten darüber.
 */
import type { SupabaseClient } from '../network/SupabaseClient';
import type { PlanetInfo } from '../world/PlayerWorldTypes';
import type { BuildingType } from '../config/BuildingTypes';
import { SHIP_TYPE_DEFS, SHIP_CLASS_NAMES } from '../config/ShipTypes';
import { calcEnergyBalance } from '../systems/EnergyBalance';
import { createFleetTabStats } from './FleetTabStats';
import { createFleetTabProduction } from './FleetTabProduction';
import { createFleetTabLayout } from './FleetTabLayout';
import { ShipHologramPreview } from '../objects/ShipHologramPreview';
import { updateHubHeader } from './PlanetHubHeader';
import { LOADING_STYLE, showHubError } from './HubOverlay';
import { devLoad, loadFleetData, buildShip } from '../network/FleetDataService';

export class PlanetMenuFleet {
  private _supabase: SupabaseClient | null;
  private _devMode: boolean;
  private _loading = false;
  private _devShips = new Map<string, number[]>();
  private _headerEl: HTMLDivElement | null = null;
  private _selectedType = 0;
  private _preview: ShipHologramPreview | null = null;
  private _planet: PlanetInfo | null = null;
  private _composition: number[] = [0, 0, 0, 0, 0, 0, 0, 0];
  private _levels: Map<BuildingType, number> = new Map();
  private _container: HTMLElement | null = null;
  private _nameEl: HTMLDivElement | null = null;
  private _classEl: HTMLDivElement | null = null;
  private _prodEl: HTMLDivElement | null = null;
  private _statsEl: HTMLDivElement | null = null;
  private _onCacheUpdate: ((d: { composition: number[]; levels: Map<BuildingType, number> }) => void) | null = null;

  constructor(supabase: SupabaseClient | null, devMode: boolean) {
    this._supabase = supabase;
    this._devMode = devMode;
  }

  setHeaderEl(el: HTMLDivElement): void { this._headerEl = el; }

  stop(): void {
    if (this._preview) { this._preview.dispose(); this._preview = null; }
  }

  async show(
    planet: PlanetInfo,
    container: HTMLElement,
    cached?: { composition: number[]; levels: Map<BuildingType, number> },
    onCacheUpdate?: (data: { composition: number[]; levels: Map<BuildingType, number> }) => void,
  ): Promise<void> {
    if (this._loading) return;
    this._loading = true;
    this._container = container;
    if (!cached) container.innerHTML = `<div style="${LOADING_STYLE}">Lade Flotte\u2026</div>`;

    try {
      const result = cached ?? (this._devMode
        ? devLoad(this._devShips, planet.id)
        : await this._loadWithError(planet));
      this._planet = planet;
      this._composition = result.composition;
      this._levels = result.levels;
      if (onCacheUpdate) this._onCacheUpdate = onCacheUpdate;
      this._onCacheUpdate?.({ composition: result.composition, levels: result.levels });
      this.stop();
      container.innerHTML = '';
      this._buildLayout(container, planet);
    } finally {
      this._loading = false;
    }
  }

  private _buildLayout(container: HTMLElement, planet: PlanetInfo): void {
    const known = this._devMode || planet.ownership === 'own';
    const energy = this._levels.size
      ? calcEnergyBalance(this._levels)
      : { modifier: 1, supply: 0, demand: 0, balance: 0, consumers: 0 };

    if (this._headerEl) updateHubHeader(this._headerEl, planet, this._levels, energy, known);

    const layout = createFleetTabLayout(
      container,
      () => this._selectedType,
      (t) => this._onSelect(t),
    );
    this._nameEl = layout.nameEl;
    this._classEl = layout.classEl;
    this._prodEl = layout.prodEl;
    this._statsEl = layout.statsEl;

    try {
      this._preview = new ShipHologramPreview(layout.canvas);
      this._preview.start();
      this._preview.setShipType(this._selectedType).catch((err) =>
        console.error('[FleetTab] Ship model load failed:', err),
      );
    } catch (err) {
      console.error('[FleetTab] Hologram init failed:', err);
    }

    this._renderProduction(layout.prodEl, planet);
    this._renderStats(layout.statsEl);
  }

  private _onSelect(type: number): void | Promise<void> {
    if (type === this._selectedType) return;
    this._selectedType = type;
    const def = SHIP_TYPE_DEFS[type];
    if (this._nameEl) this._nameEl.textContent = def.name;
    if (this._classEl) this._classEl.innerHTML = `<span style="opacity:0.8;font-size:0.85em;">Klasse</span> ${SHIP_CLASS_NAMES[def.shipClass]}`;
    if (this._prodEl && this._planet) this._renderProduction(this._prodEl, this._planet);
    if (this._statsEl) this._renderStats(this._statsEl);
    return this._preview?.setShipType(type).catch((err) =>
      console.error('[FleetTab] Ship model switch failed:', err),
    );
  }

  private _renderProduction(el: HTMLDivElement, planet: PlanetInfo): void {
    el.innerHTML = '';
    el.appendChild(createFleetTabProduction(
      this._selectedType,
      this._composition[this._selectedType] ?? 0,
      this._devMode || planet.ownership === 'own',
      { feronium: planet.feronium, chrysalium: planet.chrysalium, neurogel: planet.neurogel },
      { onBuild: () => this._planet && this._container && this._doBuild(this._selectedType) },
    ));
  }

  private _renderStats(el: HTMLDivElement): void {
    el.innerHTML = '';
    el.appendChild(createFleetTabStats(SHIP_TYPE_DEFS[this._selectedType]));
  }

  private async _loadWithError(planet: PlanetInfo) {
    if (!this._supabase) {
      return { composition: [0, 0, 0, 0, 0, 0, 0, 0], levels: new Map<BuildingType, number>() };
    }
    const result = await loadFleetData(this._supabase, planet);
    if (result.error) this._showError(result.error);
    return result;
  }

  private async _doBuild(shipType: number): Promise<void> {
    const planet = this._planet;
    const container = this._container;
    if (!planet || !container) return;

    // Schiff dreht sich zur Kamera bevor der Bau startet
    if (this._preview) await this._preview.enterBuildPose();

    // Laser-Animation + API-Call parallel starten
    const laserDone = this._preview?.startBuildAnimation() ?? Promise.resolve();

    if (this._devMode) {
      const comp = this._devShips.get(planet.id) ?? [0, 0, 0, 0, 0, 0, 0, 0];
      comp[shipType] = (comp[shipType] ?? 0) + 1;
      this._devShips.set(planet.id, comp);
      this._composition = comp;
      await laserDone;
      await this.show(planet, container, { composition: comp, levels: this._levels });
      this._onCacheUpdate?.({ composition: this._composition, levels: this._levels });
      return;
    }
    if (!this._supabase) return;

    const [result] = await Promise.all([
      buildShip(this._supabase, planet, shipType),
      laserDone,
    ]);
    if (result.error) {
      this._showError(result.error);
      await this.show(planet, container);
      return;
    }
    await this.show(planet, container, { composition: result.composition, levels: this._levels });
    this._onCacheUpdate?.({ composition: this._composition, levels: this._levels });
  }

  private _showError(msg: string): void {
    showHubError(msg);
  }
}
